/**
 * Shared header for the three intake screens (form, chat, PDFs).
 *
 * One consistent way in and out of "new project / new request":
 *  - a deterministic "← Projects" link (always returns to the list, regardless
 *    of how you got here — unlike a history-based back, which from Chat used to
 *    drop you on the Form), and
 *  - a segmented Form · Chat · PDFs switcher that shows where you are and swaps
 *    methods in place via `replace` so switching never piles up history.
 *
 * Optionally renders a compact autosave hint (form + chat) so the user can see
 * their draft is being kept.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, CloudOff, FileText, Loader2, MessageSquareText, Sparkles } from 'lucide-react';
import type { DraftAutosaveState } from '@/lib/useDraftAutosave';
import { cn } from '@/lib/cn';

export type IntakeMode = 'form' | 'chat' | 'pdf';

const MODES: { mode: IntakeMode; to: string; label: string; Icon: typeof FileText }[] = [
  { mode: 'form', to: '/projects/new', label: 'Form', Icon: FileText },
  { mode: 'chat', to: '/projects/new/chat', label: 'Chat', Icon: MessageSquareText },
  { mode: 'pdf', to: '/projects/new/pdf', label: 'PDFs', Icon: Sparkles },
];

const SUBTITLE: Record<IntakeMode, string> = {
  form: 'Fill in the request details.',
  chat: 'Describe the request in your own words — no PDFs needed.',
  pdf: 'Drop in the sales order, packing slip, or design package — AI reads them.',
};

export function IntakeHeader({
  mode,
  isPm,
  save,
}: {
  mode: IntakeMode;
  isPm: boolean;
  save?: DraftAutosaveState;
}) {
  const noun = isPm ? 'Submit a request' : 'New project';

  return (
    <header className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {isPm ? 'My requests' : 'Projects'}
        </Link>
        {save && <SaveHint save={save} />}
      </div>

      <div>
        <h1 className="text-2xl font-semibold">{noun}</h1>
        <p className="text-sm text-ink-dim mt-0.5">{SUBTITLE[mode]}</p>
      </div>

      {/* Method switcher — segmented, current highlighted. `replace` keeps the
          history stack flat so "← Projects" is always one step away. */}
      <div
        role="tablist"
        aria-label="Intake method"
        className="inline-flex items-center gap-0.5 rounded-lg border border-line bg-fill-soft p-0.5"
      >
        {MODES.map(({ mode: m, to, label, Icon }) => {
          const active = m === mode;
          return (
            <Link
              key={m}
              to={to}
              replace
              role="tab"
              aria-selected={active}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'bg-bg text-ink shadow-sm'
                  : 'text-ink-dim hover:text-ink hover:bg-bg/50',
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </Link>
          );
        })}
      </div>
    </header>
  );
}

/** Compact "Saving… / Saved / not saved" hint for the autosaving screens. */
function SaveHint({ save }: { save: DraftAutosaveState }) {
  if (save.status === 'idle' && save.savedAt == null) return null;

  if (save.status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-ink-faint">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Saving…
      </span>
    );
  }
  if (save.status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-700">
        <CloudOff className="w-3.5 h-3.5" />
        Not saved — will retry
      </span>
    );
  }
  // saved (or idle-after-a-save)
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-faint" title="Saved to your drafts">
      <Check className="w-3.5 h-3.5 text-status-complete-fg" />
      Draft saved
    </span>
  );
}
